import axios from 'axios';
import { getToken, rememberReturnTo, emitSessionExpired } from './session';

// El propio login puede responder 401/403 con credenciales invalidas: ahi no
// hay sesion que expirar, el mensaje de error lo muestra el formulario.
const AUTH_ENDPOINT = /\/user\/sign_in/;

// API Gateway devuelve el 401/403 del authorizer SIN el header
// Access-Control-Allow-Origin (el preflight OPTIONS si lo trae, la respuesta de
// error no). Como el portal y el API viven en dominios distintos, el navegador
// bloquea la respuesta antes de que llegue a JS: axios la ve como error de red,
// con `error.response` en undefined, y el status real se pierde.
//
// Mientras el API no agregue CORS a sus Gateway Responses, un fallo opaco de una
// request que llevaba token se interpreta como sesion vencida. Es una heuristica:
// si el API esta caido tambien manda al login. Se prefiere ese falso positivo a
// dejar al usuario con una pantalla vacia y sin saber que perdio la sesion.
const looksLikeBlockedAuthError = (error) => {
  if (error.response) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  const headers = (error.config && error.config.headers) || {};
  return !!(headers.Authorization || headers.authorization);
};

const $axios = axios.create({
  baseURL: process.env.RAZZLE_API_URL,
});

$axios.interceptors.request.use(
  (config) => {
    const c = { ...config };
    // Legacy paths starting with /api/ were rewritten by the old Koa proxy that
    // stripped /api before forwarding. We replicate that here so call sites stay
    // unchanged across the codebase.
    if (typeof c.url === 'string' && c.url.startsWith('/api/')) {
      c.url = c.url.replace(/^\/api/, '');
    }
    const token = getToken();
    if (token) {
      c.headers = { ...c.headers, Authorization: `Bearer ${token}` };
    }
    return c;
  },
  error => Promise.reject(error),
);

$axios.interceptors.response.use(
  response => response,
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    const status = error.response && error.response.status;
    const url = (error.config && error.config.url) || '';
    const isAuthError = status === 401
      || status === 403
      || looksLikeBlockedAuthError(error);
    const isExpired = isAuthError && !AUTH_ENDPOINT.test(url);
    if (isExpired) {
      rememberReturnTo();
      emitSessionExpired();
    }
    return Promise.reject(error);
  },
);

export default $axios;
